/**
 * Utility functions for optimizing Spotify images and other external images
 */

/**
 * Resizes Spotify image URLs to specified dimensions
 * Spotify images from i.scdn.co can be resized by appending query parameters
 * @param imageUrl - Original Spotify image URL
 * @param width - Desired width in pixels
 * @param height - Desired height in pixels (defaults to width for square images)
 * @returns Optimized image URL with dimensions
 */
/**
 * Optimizes Spotify image URLs to use appropriate sizes
 * Spotify API returns images array with different sizes: [640x640, 300x300, 64x64]
 * For playlist covers, we should use the 300x300px version (index 1) instead of full resolution
 * @param imageUrl - Original Spotify image URL
 * @param width - Desired width in pixels
 * @param height - Desired height in pixels (defaults to width for square images)
 * @returns Optimized image URL
 */
export const optimizeSpotifyImage = (
  imageUrl: string | undefined | null,
  width: number,
  height?: number
): string => {
  if (!imageUrl) {
    return "/image/image.webp"; // Fallback image
  }

  // Check if it's a Spotify image from i.scdn.co
  if (imageUrl.includes("i.scdn.co") || imageUrl.includes("spotify.com")) {
    // Spotify images are served from i.scdn.co with format: https://i.scdn.co/image/{hash}
    // The API should provide the 300x300px version (images[1]) for playlist covers
    // If we receive a full-resolution URL, we can't resize it client-side
    // The backend should return the appropriate size from the API response
    // For now, return the URL as-is - backend should provide 300x300px version
    return imageUrl;
  }

  // For other image sources, return as-is
  return imageUrl;
};

/**
 * Gets the optimal Spotify image size from API response
 * Spotify API returns images array: [large, medium, small]
 * For playlist grid: use medium (300x300) - index 1
 * For thumbnails: use small (64x64) - index 2
 */
export const getSpotifyImageUrl = (
  images: Array<{ url: string; height: number; width: number }> | undefined,
  preferredSize: "large" | "medium" | "small" = "medium"
): string => {
  if (!images || images.length === 0) {
    return "/image/image.webp";
  }

  const sizeMap = {
    large: 0,   // 640x640
    medium: 1,  // 300x300
    small: 2,   // 64x64
  };

  const index = sizeMap[preferredSize];
  const image = images[index] || images[0]; // Fallback to first available

  return image.url;
};

/**
 * Gets the appropriate image size based on context
 */
export const getImageSize = (context: "playlist-grid" | "thumbnail" | "avatar" | "hero") => {
  switch (context) {
    case "playlist-grid":
      return { width: 168, height: 168 };
    case "thumbnail":
      return { width: 64, height: 64 };
    case "avatar":
      return { width: 64, height: 64 };
    case "hero":
      return { width: 225, height: 289 };
    default:
      return { width: 168, height: 168 };
  }
};

/**
 * Creates a srcset for responsive images
 * @param imageUrl - Base image URL
 * @param sizes - Array of sizes in pixels
 * @returns srcset string
 */
export const createSrcSet = (imageUrl: string, sizes: number[]): string => {
  return sizes
    .map((size) => `${optimizeSpotifyImage(imageUrl, size, size)} ${size}w`)
    .join(", ");
};

